import _ from 'lodash';
import { ComputedRef, computed, reactive, UnwrapRef, watch } from '@vue/composition-api';
import i18n, { i18n_t } from '@/locales/i18n';
import tips from '@/renderer/scripts/utils/tips';
import store from '@/renderer/store';
import smh, { SMH } from '@/renderer/scripts/smh';
import storage from '@/renderer/scripts/utils/storage';
import { IS_BROWSER } from '@/common/constant';
import spaceATM from '@/renderer/scripts/utils/AccessTokenManager/space';

type Handler = {
  isEqual: ComputedRef<boolean>;
  formData: UnwrapRef<SMH.WatermarkConfig>;
  saveWatermarkConfig: (watermarkOptions: SMH.WatermarkConfig, operType?: boolean) => void;
};

/**
 * @description
 * @export
 * @param {boolean} [checkPermission=0] 是否仅列出所属团队
 * @return {*}  {Handler}
 */
export default function handler(): Handler {
  const featureNotSupported = computed(() => store.getters.editionFlag !== 'enterprise');
  const defaultWatermarkType = computed(() => (featureNotSupported.value ? 1 : 0));

  // 水印设置默认值
  const defaultPreviewWaterwarkConfig = computed(() => {
    return {
      enablePreviewWatermark: store.getters.previewWatermarkConfig.enablePreviewWatermark,
      previewWatermarkType: store.getters.previewWatermarkConfig.previewWatermarkType || defaultWatermarkType.value,
    };
  });
  const defaultDownloadWaterwarkConfig = computed(() => {
    return {
      enableDownloadWatermark: store.getters.downloadWatermarkConfig.enableDownloadWatermark,
      downloadWatermarkType: store.getters.downloadWatermarkConfig.downloadWatermarkType || defaultWatermarkType.value,
    };
  });
  const defaultShareWaterwarkConfig = computed(() => {
    return {
      enableShareWatermark: store.getters.shareWatermarkConfig.enableShareWatermark,
      shareWatermarkType: store.getters.shareWatermarkConfig.shareWatermarkType,
    };
  });

  // 水印表单值
  const formData = reactive<SMH.WatermarkConfig>({
    enablePreviewWatermark: true,
    previewWatermarkType: 0,
    enableDownloadWatermark: true,
    downloadWatermarkType: 0,
    enableShareWatermark: true,
    shareWatermarkType: 1,
  });

  // 是否修改配置项
  const isEqual = computed(() => {
    return _.isEqual(
      {
        ...defaultPreviewWaterwarkConfig.value,
        ...defaultDownloadWaterwarkConfig.value,
        ...defaultShareWaterwarkConfig.value,
      },
      formData,
    );
  });
  const methods = {
    init() {
      Object.assign(formData, {
        ...defaultPreviewWaterwarkConfig.value,
        ...defaultDownloadWaterwarkConfig.value,
        ...defaultShareWaterwarkConfig.value,
      });
    },
    async saveWatermarkConfig(watermarkOptions: SMH.WatermarkConfig) {
      try {
        let changePreviewWatermark = false; // 标志：预览水印进行了修改
        await smh.userAPI.updateOrgInfo({
          name: store.getters.orgName,
          extensionData: {
            watermarkOptions,
          },
        });
        const {
          enablePreviewWatermark,
          previewWatermarkType,
          enableDownloadWatermark,
          downloadWatermarkType,
          enableShareWatermark,
          shareWatermarkType,
        } = formData;

        // 处理水印上报和store值
        if (
          !_.isEqual(defaultPreviewWaterwarkConfig.value, {
            enablePreviewWatermark,
            previewWatermarkType,
          })
        ) {
          changePreviewWatermark = true;
          store.commit('SET_PREVIEW_WATERMARK_CONFIG', {
            enablePreviewWatermark,
            previewWatermarkType,
          });
          reporter.report('watermark_settings', {
            switch_status: enablePreviewWatermark ? 'preview_detail_open' : 'preview_detail_close',
            setting_details: previewWatermarkType === 0 ? 'preview_setting_user' : 'preview_setting_busines',
          });
        }
        if (
          !_.isEqual(defaultDownloadWaterwarkConfig.value, {
            enableDownloadWatermark,
            downloadWatermarkType,
          })
        ) {
          store.commit('SET_DOWNLOAD_WATERMARK_CONFIG', {
            enableDownloadWatermark,
            downloadWatermarkType,
          });
          reporter.report('watermark_settings', {
            switch_status: enableDownloadWatermark ? 'photo_detail_open' : 'photo_detail_close',
            setting_details: downloadWatermarkType === 0 ? 'photo_setting_user' : 'photo_setting_business',
          });
        }
        if (
          !_.isEqual(defaultShareWaterwarkConfig.value, {
            enableShareWatermark,
            shareWatermarkType,
          })
        ) {
          store.commit('SET_SHARE_WATERMARK_CONFIG', {
            enableShareWatermark,
            shareWatermarkType,
          });
          let setting_details = '';
          switch (shareWatermarkType) {
            case 1:
              setting_details = 'link_setting_share';
              break;
            case 3:
              setting_details = 'link_setting_custom';
              break;
            default:
              setting_details = 'link_setting_receive';
          }
          reporter.report('watermark_settings', {
            switch_status: enableShareWatermark ? 'link_detail_open' : 'link_detail_close',
            setting_details,
          });
        }
        methods.changePreviewWatermark({ changePreviewWatermark });
        tips.success(i18n_t('保存成功'));
      } catch (e: MsgError) {
        tips.error(e);
      }
    },
    changePreviewWatermark(options?: { changePreviewWatermark: boolean }) {
      if (IS_BROWSER) {
        storage.set(storage.StoreKey.WATERMARK_FLAG, 'Y');
      } else if (options?.changePreviewWatermark) {
        // FIX: PC端修改图片预览水印需要清理accessToken，后面再预览图片需要重新获取新的accessToken
        // 原因：若用原来的accessToken会导致electron web用的原来的浏览器缓存，导致水印信息无法实时生效
        spaceATM.clear();
      }
    },
  };
  methods.init();
  watch(
    defaultPreviewWaterwarkConfig,
    (newVal: CoFile.PreviewWatermarkConfig, oldVal: CoFile.PreviewWatermarkConfig) => {
      if (!_.isEqual(newVal, oldVal)) {
        Object.assign(formData, {
          ...newVal,
        });
      }
    },
  );
  watch(
    defaultDownloadWaterwarkConfig,
    (newVal: CoFile.DownloadWatermarkConfig, oldVal: CoFile.DownloadWatermarkConfig) => {
      if (!_.isEqual(newVal, oldVal)) {
        Object.assign(formData, {
          ...newVal,
        });
      }
    },
  );
  watch(defaultShareWaterwarkConfig, (newVal: CoFile.ShareWatermarkConfig, oldVal: CoFile.ShareWatermarkConfig) => {
    if (!_.isEqual(newVal, oldVal)) {
      Object.assign(formData, {
        ...newVal,
      });
    }
  });
  return {
    ...methods,
    formData,
    isEqual,
  };
}
